import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WishlistSummary = ({ 
  items = [], 
  budgetLimit = 0, 
  onSetBudget = () => {},
  onExportWishlist = () => {},
  onShareWishlist = () => {}
}) => {
  const totalValue = items?.reduce((sum, item) => sum + item?.currentPrice, 0);
  const totalSavings = items?.reduce((sum, item) => {
    return sum + Math.max(0, item?.originalPrice - item?.currentPrice);
  }, 0);
  
  const priceDropSavings = items?.reduce((sum, item) => {
    return sum + Math.max(0, -item?.priceChange);
  }, 0);

  const highPriorityItems = items?.filter(item => item?.priority === 'high')?.length;
  const mediumPriorityItems = items?.filter(item => item?.priority === 'medium')?.length;
  const lowPriorityItems = items?.filter(item => item?.priority === 'low')?.length;

  const budgetUsed = (totalValue / budgetLimit) * 100;
  const budgetColor = budgetUsed > 100 ? 'text-error' : budgetUsed > 80 ? 'text-warning' : 'text-success';

  const categories = items?.reduce((acc, item) => {
    acc[item.category] = (acc?.[item?.category] || 0) + 1;
    return acc;
  }, {});

  const StatCard = ({ icon, label, value, color = 'text-foreground', subtext = null }) => (
    <div className="bg-background border border-border rounded-lg p-4">
      <div className="flex items-center space-x-3 mb-2">
        <div className="p-2 bg-accent/10 rounded-full">
          <Icon name={icon} size={20} color="var(--color-accent)" />
        </div>
        <div>
          <div className={`text-lg font-bold ${color}`}>{value}</div>
          <div className="text-sm text-muted-foreground">{label}</div>
        </div>
      </div>
      {subtext && (
        <div className="text-xs text-muted-foreground">{subtext}</div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon="Package"
          label="Total Items"
          value={items?.length}
          subtext={`${highPriorityItems} high priority`}
        />
        <StatCard
          icon="DollarSign"
          label="Total Value"
          value={`$${totalValue?.toFixed(2)}`}
          subtext={totalSavings > 0 ? `$${totalSavings?.toFixed(2)} in discounts` : null}
        />
        <StatCard
          icon="TrendingDown"
          label="Price Drop Savings"
          value={`$${priceDropSavings?.toFixed(2)}`}
          color="text-success"
          subtext={priceDropSavings > 0 ? 'Since adding to wishlist' : 'No recent drops'}
        />
        <StatCard
          icon="Target"
          label="Budget Status"
          value={budgetLimit > 0 ? `${budgetUsed?.toFixed(0)}%` : 'Not Set'}
          color={budgetLimit > 0 ? budgetColor : 'text-muted-foreground'}
          subtext={budgetLimit > 0 ? `$${(budgetLimit - totalValue)?.toFixed(2)} remaining` : 'Set a budget limit'}
        />
      </div>
      {/* Priority Breakdown */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
          <Icon name="Flag" size={20} />
          <span>Priority Breakdown</span>
        </h3>
        
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-lg font-bold text-error">{highPriorityItems}</span>
            </div>
            <div className="text-sm font-medium text-foreground">High Priority</div>
            <div className="text-xs text-muted-foreground">Must have upgrades</div>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-lg font-bold text-warning">{mediumPriorityItems}</span>
            </div>
            <div className="text-sm font-medium text-foreground">Medium Priority</div>
            <div className="text-xs text-muted-foreground">Nice to have</div>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-lg font-bold text-success">{lowPriorityItems}</span>
            </div>
            <div className="text-sm font-medium text-foreground">Low Priority</div>
            <div className="text-xs text-muted-foreground">Future upgrades</div>
          </div>
        </div>
      </div>
      {/* Category Distribution */}
      {Object.keys(categories)?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
            <Icon name="BarChart3" size={20} />
            <span>Categories</span>
          </h3>
          
          <div className="space-y-3">
            {Object.entries(categories)?.map(([category, count]) => {
              const percentage = (count / items?.length) * 100;
              return (
                <div key={category} className="flex items-center space-x-3">
                  <div className="w-24 text-sm text-foreground font-medium">
                    {category}
                  </div>
                  <div className="flex-1 bg-muted rounded-full h-2">
                    <div 
                      className="bg-accent h-2 rounded-full transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <div className="w-12 text-sm text-muted-foreground text-right">
                    {count} items
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* Budget Management */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
          <Icon name="Wallet" size={20} />
          <span>Budget Management</span>
        </h3>
        
        {budgetLimit > 0 ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Budget Limit</span>
              <span className="text-sm font-medium text-foreground">${budgetLimit?.toFixed(2)}</span>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Used</span>
                <span className={`font-medium ${budgetColor}`}>
                  ${totalValue?.toFixed(2)} ({budgetUsed?.toFixed(0)}%)
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${
                    budgetUsed > 100 ? 'bg-error' : budgetUsed > 80 ? 'bg-warning' : 'bg-success'
                  }`}
                  style={{ width: `${Math.min(budgetUsed, 100)}%` }}
                />
              </div>
            </div>
            
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onSetBudget}
                iconName="Edit2"
                iconPosition="left"
              >
                Update Budget
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground mb-3">
              Set a budget limit to track your spending and get notifications when you're close to your limit.
            </p>
            <Button
              variant="default"
              size="sm"
              onClick={onSetBudget}
              iconName="Plus"
              iconPosition="left"
            >
              Set Budget Limit
            </Button>
          </div>
        )}
      </div>
      {/* Quick Actions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
          <Icon name="Zap" size={20} />
          <span>Quick Actions</span>
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button
            variant="outline"
            onClick={onExportWishlist}
            iconName="Download"
            iconPosition="left"
            className="justify-start"
          >
            Export Wishlist
          </Button>
          <Button
            variant="outline"
            onClick={onShareWishlist}
            iconName="Share2"
            iconPosition="left"
            className="justify-start"
          >
            Share Wishlist
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WishlistSummary;